/**
 * MobileDrawer Component
 *
 * Hamburger menu and full-screen overlay for mobile navigation (<768px).
 */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Menu,
  Home,
  DollarSign,
  Users,
  Factory,
  BarChart3,
  Settings,
  User,
  LogOut,
  ShoppingCart,
  FileText,
  Tag,
  Package,
  Gauge,
  Layers,
  ChevronDown,
  Truck,
  CalendarRange,
  Beaker,
  Wheat,
  Scissors,
  ClipboardList,
  Sprout,
  Flame,
  Timer,
  Warehouse,
  Building2,
  Receipt,
  Calculator,
  Dna,
  Workflow,
  TrendingUp,
  Zap,
  X,
} from 'lucide-react'

// Mobile navigation - includes Deliveries (mobile-only feature)
const mainNavigation = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    path: '/'
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: ClipboardList,
    path: '/tasks'
  },
  {
    id: 'deliveries',
    label: 'Deliveries',
    icon: Truck,
    path: '/deliveries'
  },
  {
    id: 'quick-order',
    label: 'Quick Order',
    icon: Zap,
    path: '/sales/quick'
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: DollarSign,
    children: [
      { id: 'sales-timeline', label: 'Timeline', path: '/sales/timeline', icon: TrendingUp },
      { id: 'orders', label: 'Orders', path: '/sales/orders', icon: ShoppingCart },
      { id: 'invoicing', label: 'Invoicing', path: '/sales/invoicing', icon: FileText },
      { id: 'pricing', label: 'Pricing', path: '/sales/pricing', icon: Tag }
    ]
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    path: '/customers'
  },
  {
    id: 'production',
    label: 'Production',
    icon: Factory,
    children: [
      { id: 'timeline', label: 'Timeline', path: '/production', icon: CalendarRange },
      { id: 'strains', label: 'Strains', path: '/production/strains', icon: Dna },
      { id: 'process', label: 'Processes', path: '/production/process', icon: Workflow },
      { id: 'liquid-cultures', label: 'Liquid Cultures', path: '/production/lc', icon: Beaker },
      { id: 'spawn', label: 'Spawn', path: '/production/spawn', icon: Wheat },
      { id: 'substrate', label: 'Substrate', path: '/production/substrate', icon: Layers },
      { id: 'heat-cycling', label: 'Heat Cycling', path: '/production/heat-cycling', icon: Flame },
      { id: 'inoculations', label: 'Inoculations', path: '/production/inoculations', icon: Sprout },
      { id: 'incubation', label: 'Incubation', path: '/production/incubation', icon: Timer },
      { id: 'harvests', label: 'Harvests', path: '/production/harvests', icon: Scissors }
    ]
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Warehouse,
    children: [
      { id: 'overview', label: 'Overview', path: '/inventory', icon: Gauge },
      { id: 'items', label: 'Items', path: '/inventory/items', icon: Package },
      { id: 'purchases', label: 'Purchases', path: '/inventory/purchases', icon: Receipt },
      { id: 'suppliers', label: 'Suppliers', path: '/inventory/suppliers', icon: Building2 },
    ]
  },
  {
    id: 'costs',
    label: 'Costs',
    icon: Calculator,
    children: [
      { id: 'summary', label: 'Summary', path: '/costs/summary', icon: BarChart3 },
      { id: 'fixed', label: 'Fixed Costs', path: '/costs/fixed', icon: Receipt }
    ]
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    path: '/reporting'
  }
]

const settingsNavigation = [
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { id: 'products', label: 'Products', path: '/settings/products', icon: Package },
      { id: 'system', label: 'System', path: '/settings/system', icon: Gauge }
    ]
  }
]

function MobileNavItem({ item, isActive, onNavigate }) {
  const [isOpen, setIsOpen] = useState(isActive)
  const Icon = item.icon

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'w-full flex items-center gap-3 px-4 py-3 rounded-lg',
            'hover:bg-accent/50 transition-colors',
            isActive && 'bg-accent'
          )}
        >
          <Icon className="w-5 h-5 text-muted-foreground" />
          <span className="flex-1 text-left">{item.label}</span>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>
        {isOpen && (
          <div className="mt-1 ml-6 space-y-1">
            {item.children.map((child) => {
              const ChildIcon = child.icon
              return (
                <button
                  key={child.id}
                  onClick={() => onNavigate(child.path)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 rounded-lg',
                    'hover:bg-accent/50 transition-colors text-sm',
                    child.isActive && 'bg-accent font-medium'
                  )}
                >
                  <ChildIcon className="w-4 h-4 text-muted-foreground" />
                  <span>{child.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <button
      onClick={() => onNavigate(item.path)}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 rounded-lg',
        'hover:bg-accent/50 transition-colors',
        isActive && 'bg-accent font-medium'
      )}
    >
      <Icon className="w-5 h-5 text-muted-foreground" />
      <span>{item.label}</span>
    </button>
  )
}

export default function MobileDrawer({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  const handleNavigate = (path) => {
    navigate(path)
    setIsOpen(false)
  }

  const handleLogout = () => {
    setIsOpen(false)
    onLogout()
  }

  const isPathActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const hasActiveChild = (children) => {
    return children?.some(child => isPathActive(child.path))
  }

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-border z-50 md:hidden flex items-center px-4 gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10"
          onClick={() => setIsOpen(true)}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>

        {/* Logo in mobile header */}
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <img
            src="/odinlogo.png"
            alt="Odin"
            className="w-8 h-8 object-contain"
          />
          <span className="font-semibold text-foreground">Odin</span>
        </div>
      </div>

      {/* Full-screen overlay */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-white flex flex-col transition-all duration-200 ease-out md:hidden',
          isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        )}
      >
        {/* Overlay Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <img
              src="/odinlogo.png"
              alt="Odin Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h2 className="font-semibold text-foreground">Odin</h2>
              <p className="text-xs text-muted-foreground">Farm Management</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex flex-col flex-1 p-4 overflow-y-auto">
          {/* Main Navigation */}
          <div className="space-y-1">
            {mainNavigation.map((item) => (
              <MobileNavItem
                key={item.id}
                item={{
                  ...item,
                  children: item.children?.map(child => ({
                    ...child,
                    isActive: isPathActive(child.path)
                  }))
                }}
                isActive={item.children ? hasActiveChild(item.children) : isPathActive(item.path)}
                onNavigate={handleNavigate}
              />
            ))}
          </div>

          {/* Divider */}
          <div className="my-4 border-t border-border" />

          {/* Settings */}
          <div className="space-y-1">
            {settingsNavigation.map((item) => (
              <MobileNavItem
                key={item.id}
                item={{
                  ...item,
                  children: item.children?.map(child => ({
                    ...child,
                    isActive: isPathActive(child.path)
                  }))
                }}
                isActive={item.children ? hasActiveChild(item.children) : isPathActive(item.path)}
                onNavigate={handleNavigate}
              />
            ))}
          </div>

          {/* User Section — pinned to bottom */}
          <div className="mt-auto pt-4 border-t border-border">
            <div className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground">
              <User className="w-5 h-5" />
              <span className="truncate">{user?.email?.split('@')[0] || 'User'}</span>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors text-sm"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign out</span>
            </button>
          </div>
        </nav>
      </div>
    </>
  )
}
